import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, MoreHorizontal, Check, Plus, Users, Trash2, Copy, Edit2, X } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
export interface SavedView {
  id: string;
  name: string;
  /** 'preset' views are built in, listed under their `group` heading, and can't be edited */
  type: 'default' | 'mine' | 'team' | 'preset';
  creator?: string;
  isShared?: boolean;
  group?: string;
}
interface SavedViewsDropdownProps {
  currentView: string;
  onViewSelect: (view: SavedView) => void;
  onSaveView: (name: string, isShared: boolean) => void;
  onDeleteView?: (viewId: string) => void;
  onDuplicateView?: (viewId: string) => void;
  onRenameView?: (viewId: string, newName: string) => void;
  hasActiveFilters: boolean;
  savedViews: SavedView[];
  className?: string;
}
export function SavedViewsDropdown({
  currentView,
  onViewSelect,
  onSaveView,
  onDeleteView,
  onDuplicateView,
  onRenameView,
  hasActiveFilters,
  savedViews,
  className = ''
}: SavedViewsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [renamingViewId, setRenamingViewId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsSaving(false);
        setOpenMenuId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Close overflow menu when clicking outside
  useEffect(() => {
    function handleClickOutsideMenu(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    }
    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutsideMenu);
    }
    return () => document.removeEventListener('mousedown', handleClickOutsideMenu);
  }, [openMenuId]);
  // Focus rename input when entering rename mode
  useEffect(() => {
    if (renamingViewId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingViewId]);
  const handleStartRename = (view: SavedView) => {
    setRenamingViewId(view.id);
    setRenameValue(view.name);
    setOpenMenuId(null);
  };
  const handleConfirmRename = () => {
    if (renamingViewId && renameValue.trim()) {
      onRenameView?.(renamingViewId, renameValue.trim());
    }
    setRenamingViewId(null);
    setRenameValue('');
  };
  const handleCancelRename = () => {
    setRenamingViewId(null);
    setRenameValue('');
  };
  const handleSave = () => {
    if (newViewName.trim()) {
      onSaveView(newViewName, isShared);
      setIsSaving(false);
      setNewViewName('');
      setIsShared(false);
      setIsOpen(false);
    }
  };
  const handleCancelSave = () => {
    setIsSaving(false);
    setNewViewName('');
    setIsShared(false);
  };
  const defaultView = savedViews.find(v => v.type === 'default') || {
    id: 'all',
    name: 'All Subscriptions',
    type: 'default'
  };
  const myViews = savedViews.filter(v => v.type === 'mine');
  // Built-in views shown under their own headings, e.g. "Call lists"
  const presetGroups = savedViews.filter(v => v.type === 'preset').reduce<{
    heading: string;
    views: SavedView[];
  }[]>((groups, view) => {
    const heading = view.group || 'Presets';
    const existing = groups.find(group => group.heading === heading);
    if (existing) existing.views.push(view);else groups.push({
      heading,
      views: [view]
    });
    return groups;
  }, []);
  const teamViews = savedViews.filter(v => v.type === 'team');
  const renderViewItem = (view: SavedView) => {
    const isActive = currentView === view.name;
    const isDefault = view.type === 'default';
    const isRenaming = renamingViewId === view.id;
    if (isRenaming) {
      return <div key={view.id} className="flex items-center gap-2 px-3 py-2 rounded-md" data-id="element-4452" data-mp="197m3n4o6rn31">
          <input ref={renameInputRef} type="text" value={renameValue} onChange={e => setRenameValue(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter') handleConfirmRename();
          if (e.key === 'Escape') handleCancelRename();
        }} className="flex-1 h-7 px-2 text-sm border border-[#0EA5E9] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] bg-white" data-id="element-4453" data-mp="199f9ihqz639z" />
          <button onClick={handleConfirmRename} disabled={!renameValue.trim()} className="p-1 rounded-md text-[#10B981] hover:bg-[#D1FAE5] transition-colors disabled:opacity-40" data-id="element-4454" data-mp="2un1j5s6fwr1a">
            <Check className="w-4 h-4" data-id="element-4455" data-mp="dz0hy2p2307g" />
          </button>
          <button onClick={handleCancelRename} className="p-1 rounded-md text-[#94A3B8] hover:bg-[#F1F5F9] transition-colors" data-id="element-4456" data-mp="97a9615roki5">
            <X className="w-4 h-4" data-id="element-4457" data-mp="47xkrbz8z5dg" />
          </button>
        </div>;
    }
    return <div key={view.id} className={`group flex items-center justify-between px-3 py-2 rounded-md cursor-pointer transition-colors ${isActive ? 'bg-[#F0F9FF]' : 'hover:bg-[#F8FAFC]'}`} onClick={() => {
      onViewSelect(view);
      setIsOpen(false);
    }} data-id="element-4458" data-mp="7dlo2v45roo">
        <div className="flex items-center gap-2 min-w-0 flex-1" data-id="element-4459" data-mp="3mg528hcu1jfm">
          {isActive && <div className="w-1 h-4 bg-[#0EA5E9] rounded-full flex-shrink-0" data-id="element-4460" data-mp="3hh21va7ss1dz" />}
          <div className={`flex flex-col min-w-0 ${!isActive ? 'pl-3' : ''}`} data-id="element-4461" data-mp="1klnsqtthmdsd">
            <span className={`text-sm truncate ${isActive ? 'font-medium text-[#0EA5E9]' : 'text-[#0F172A]'}`} data-id="element-4462" data-mp="1fnm4luh7foa1">
              {view.name}
            </span>
            {view.type === 'team' && <div className="flex items-center gap-1.5 mt-0.5" data-id="element-4463" data-mp="3evb0941kobvc">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0]" data-id="element-4464" data-mp="3ewvsgn0eziq1">
                  Shared
                </span>
                <span className="text-[11px] text-[#94A3B8] truncate" data-id="element-4465" data-mp="352n3uaei89x5">
                  by {view.creator}
                </span>
              </div>}
          </div>
        </div>

        {!isDefault && view.type !== 'preset' && <div className="relative" onClick={e => e.stopPropagation()} data-id="element-4466" data-mp="3ju5s0vhiparl">
            <button onClick={() => setOpenMenuId(openMenuId === view.id ? null : view.id)} className={`p-1 rounded-md text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#E2E8F0] opacity-0 group-hover:opacity-100 transition-all ${openMenuId === view.id ? 'opacity-100 bg-[#E2E8F0]' : ''}`} data-id="element-4467" data-mp="3jvystu5g90ro">
              <MoreHorizontal className="w-4 h-4" data-id="element-4468" data-mp="2chnbbbrdm2x9" />
            </button>

            {openMenuId === view.id && <div ref={menuRef} className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border border-[#E2E8F0] z-50 py-1 overflow-hidden" data-id="element-4469" data-mp="24uyki91q4j3m">
                {view.type === 'mine' && <button onClick={() => handleStartRename(view)} className="w-full px-3 py-2 text-left text-[13px] text-[#0F172A] hover:bg-[#F8FAFC] flex items-center gap-2" data-id="element-4470" data-mp="2euh1cgmc7org">
                    <Edit2 className="w-3.5 h-3.5 text-[#64748B]" data-id="element-4471" data-mp="1m7d3cvlnwap6" />
                    Rename
                  </button>}
                <button onClick={() => {
            onDuplicateView?.(view.id);
            setOpenMenuId(null);
          }} className="w-full px-3 py-2 text-left text-[13px] text-[#0F172A] hover:bg-[#F8FAFC] flex items-center gap-2" data-id="element-4472" data-mp="1h8s34w6etr0q">
                  <Copy className="w-3.5 h-3.5 text-[#64748B]" data-id="element-4473" data-mp="13fu1is3dj2oq" />
                  Duplicate
                </button>
                {view.type === 'mine' && <button onClick={() => {
            onDeleteView?.(view.id);
            setOpenMenuId(null);
          }} className="w-full px-3 py-2 text-left text-[13px] text-[#EF4444] hover:bg-[#FEF2F2] flex items-center gap-2 border-t border-[#E2E8F0] mt-1" data-id="element-4474" data-mp="yfo7545pz22y">
                    <Trash2 className="w-3.5 h-3.5" data-id="element-4475" data-mp="mvtxts61t4ao" />
                    Delete
                  </button>}
                {view.type === 'team' && <button onClick={() => {
            // Implement remove from my views logic
            setOpenMenuId(null);
          }} className="w-full px-3 py-2 text-left text-[13px] text-[#EF4444] hover:bg-[#FEF2F2] flex items-center gap-2 border-t border-[#E2E8F0] mt-1" data-id="element-4476" data-mp="qy8fwa1w800w">
                    <X className="w-3.5 h-3.5" data-id="element-4477" data-mp="15ltk67ai85ae" />
                    Remove
                  </button>}
              </div>}
          </div>}
      </div>;
  };
  return <div className={`relative ${className}`} ref={dropdownRef} data-id="element-4478" data-mp="1an94fx4uy7rq">
      <Button variant="outline" size="md" onClick={() => setIsOpen(!isOpen)} className={`justify-between min-w-[160px] ${isOpen ? 'bg-[#F8FAFC] ring-2 ring-[#E0F2FE] border-[#0EA5E9]' : ''}`} iconRight={<ChevronDown className={`w-4 h-4 text-[#64748B] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-4480" data-mp="344yh6y1tgoi5" />} data-id="element-4479" data-mp="1eo2twuxy7umq">
        <span className="truncate max-w-[140px]" data-id="element-4481" data-mp="2z3hfj0bicy0i">{'Views'}</span>
      </Button>

      {isOpen && <div className="absolute top-full left-0 mt-2 w-[280px] bg-white border border-[#E2E8F0] rounded-lg shadow-[0_4px_12px_rgba(15,23,42,0.1)] z-[100] animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-4482" data-mp="2v3qpwzxpwnxk">
          <div className="p-2 space-y-1" data-id="element-4483" data-mp="3mtlkpc4ch5tm">
            {/* Default View */}
            {renderViewItem(defaultView)}

            {/* Preset views, e.g. Call lists */}
            {presetGroups.map(group => <div key={group.heading} className="mt-3" data-id="element-4484" data-mp="3e08ifjke4ruv">
                <div className="px-3 py-1.5 text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-4485" data-mp="393rrkhlt3ze3">
                  {group.heading}
                </div>
                {group.views.map(renderViewItem)}
              </div>)}

            {/* My Views */}
            {myViews.length > 0 && <div className="mt-3" data-id="element-4486" data-mp="3g4v4qmhb9efm">
                <div className="px-3 py-1.5 text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-4487" data-mp="3g5wqbkvydjbl">
                  My Views
                </div>
                {myViews.map(renderViewItem)}
              </div>}
          </div>

          {/* Footer / Save View Form */}
          {(hasActiveFilters || isSaving) && <div className="border-t border-[#E2E8F0] p-3 bg-[#F8FAFC] rounded-b-lg" data-id="element-4488" data-mp="3l6kgk4ccxw4r">
              {isSaving ? <div className="space-y-3" data-id="element-4489" data-mp="2ws1f0h5vj0i">
                  <div data-id="element-4490" data-mp="3u5ny33vvm3h5">
                    <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-4491" data-mp="3u4cylg9grhvu">
                      View Name
                    </label>
                    <input type="text" value={newViewName} onChange={e => setNewViewName(e.target.value)} placeholder="e.g. VIP Failed Payments" className="w-full h-8 px-3 text-sm border border-[#CBD5E1] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-[#0EA5E9]" autoFocus data-id="element-4492" data-mp="7pl8vw40l5ga" />
                  </div>

                  <div className="flex items-center gap-2" data-id="element-4493" data-mp="10brxkefh4xwk">
                    <button onClick={() => setIsShared(!isShared)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${isShared ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-4494" data-mp="10a8cmaivhyog">
                      <span className="inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200" style={{
                transform: isShared ? 'translateX(22px)' : 'translateX(4px)'
              }} data-id="element-4495" data-mp="vcorcqnrcln0" />
                    </button>
                    <span className="text-[13px] text-[#475569]" data-id="element-4496" data-mp="mdl7vlkoi47z">
                      Share with team
                    </span>
                  </div>

                  <div className="flex items-center gap-2 pt-1" data-id="element-4497" data-mp="clp908arqa2p">
                    <Button variant="primary" size="sm" onClick={handleSave} disabled={!newViewName.trim()} className="flex-1" data-id="element-4498" data-mp="3us3k7zhnk5xe">
                      Save
                    </Button>
                    <Button variant="secondary" size="sm" onClick={handleCancelSave} className="flex-1" data-id="element-4499" data-mp="3nvbiu19pady">
                      Cancel
                    </Button>
                  </div>
                </div> : <button onClick={() => setIsSaving(true)} className="w-full flex items-center justify-center gap-2 text-[13px] font-medium text-[#0EA5E9] hover:text-[#0284C7] py-1 transition-colors" data-id="element-4500" data-mp="20iwxeumyhs9y">
                  <Plus className="w-4 h-4" data-id="element-4501" data-mp="1rhgpqunj049f" />
                  Save Current View
                </button>}
            </div>}
        </div>}
    </div>;
}